import { forwardRef, type CSSProperties } from 'react';
import { DatePicker, DateRangePicker } from '@vnetwork/ui/primitives';

/** `YYYY-MM-DD` from LOCAL date parts — `toISOString()` would shift to UTC. */
function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse the leading `YYYY-MM-DD` of a value as a LOCAL day; anything else → null. */
function parseDateString(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

const formatDisplay = (date: Date): string =>
  `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

/** The slice of a change event that form handlers in this app read. */
export interface DateInputChangeEvent {
  target: { value: string; name?: string };
}

export interface DateInputProps {
  /** `YYYY-MM-DD`, or empty for no date. */
  value?: string | null;
  /**
   * Event-shaped on purpose: it is a drop-in for `<Input type="date">`, so
   * existing `(e) => set(e.target.value)` handlers and RHF `field.onChange`
   * keep working. `e.target.value` is `YYYY-MM-DD`, or `''` when cleared.
   */
  onChange?: (event: DateInputChangeEvent) => void;
  /** Earliest / latest selectable day (`YYYY-MM-DD`). */
  min?: string;
  max?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Show the clear (×) button. @default true */
  clearable?: boolean;
  className?: string;
  onBlur?: () => void;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/**
 * Date field for forms whose state / API payload is a `YYYY-MM-DD` string:
 * the `@vnetwork/ui` `DatePicker` (calendar popover, localized, DD/MM/YYYY
 * display) behind the same string contract the native date input had.
 */
export const DateInput = forwardRef<HTMLButtonElement, DateInputProps>(function DateInput(
  {
    value,
    onChange,
    min,
    max,
    name,
    invalid,
    onBlur,
    'aria-invalid': ariaInvalid,
    ...rest
  },
  ref,
) {
  return (
    <DatePicker
      ref={ref}
      {...rest}
      value={parseDateString(value)}
      minDate={parseDateString(min) ?? undefined}
      maxDate={parseDateString(max) ?? undefined}
      invalid={invalid ?? ariaInvalid}
      format={formatDisplay}
      onChange={(date) => {
        onChange?.({ target: { value: date ? toDateString(date) : '', name } });
        onBlur?.();
      }}
    />
  );
});

export interface DateRangeInputProps {
  /** Range start / end as `YYYY-MM-DD`, or empty. */
  from?: string | null;
  to?: string | null;
  /** Both ends as `YYYY-MM-DD` (`''` when unset). Fires once both ends are picked, and on clear. */
  onChange: (from: string, to: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** Sizing for the trigger wrapper — the picker itself fills it. */
  style?: CSSProperties;
  'aria-label'?: string;
}

/**
 * From–to filter as ONE control: the `@vnetwork/ui` `DateRangePicker` over two
 * `YYYY-MM-DD` strings. Use instead of a pair of date inputs with a dash.
 */
export function DateRangeInput({ from, to, onChange, style, ...rest }: DateRangeInputProps) {
  return (
    <div style={style}>
      <DateRangePicker
        {...rest}
        value={{ from: parseDateString(from), to: parseDateString(to) }}
        format={formatDisplay}
        onChange={(range) =>
          onChange(
            range.from ? toDateString(range.from) : '',
            range.to ? toDateString(range.to) : '',
          )
        }
      />
    </div>
  );
}
